import { useState } from "react";
import { useParams } from "react-router";
import { isStaleWrite } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { ShellFrame } from "../shell/ShellFrame";
import { ErrorState, LoadingState } from "../shell/states";
import { cancelWaitlistEntry, useWaitlistStatus, type WaitlistStatus } from "../waitlist/statusApi";

/**
 * `/w/:venueId/:token` — the no-login waitlist live-status page every waitlist message links to
 * (waitlist-live-status). It resolves the entry by its per-entry HMAC token (venue-scoped) and shows the
 * diner's OWN position/estimate/state + a cancel button — no login, no contact PII (R5). The advisory
 * estimate is labelled an estimate, never a promise (R1); an empty queue reads "no wait", not a 0 error.
 * State labels map 1:1 onto §C.9 (`NOTIFIED` → "table ready"). A tampered/expired/unknown link renders one
 * generic "no longer active" state (R2/R5). FR/EN via @repo/i18n; venue-local.
 */
export function WaitlistStatusPage() {
  const { venueId = "", token = "" } = useParams();
  const query = useWaitlistStatus(venueId, token);

  const body = (() => {
    if (query.isLoading) return <LoadingState />;
    if (query.isError && !query.data) return <ErrorState onRetry={() => void query.refetch()} />;
    const status = query.data;
    if (!status) return <ErrorState onRetry={() => void query.refetch()} />;
    if (!status.active) return <GoneState />;
    return (
      <ActiveStatus
        status={status}
        venueId={venueId}
        token={token}
        onReload={() => void query.refetch()}
      />
    );
  })();

  return <ShellFrame venueName={query.data?.venueName ?? undefined}>{body}</ShellFrame>;
}

function GoneState() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="wl-gone" data-testid="waitlist-status-gone">
      <h2 id="wl-gone">{t("widget.status.gone.title")}</h2>
      <p>{t("widget.status.gone.body")}</p>
    </section>
  );
}

/** The pre-seated open states still show a live position; SEATED + terminals show their own message. */
function isOpenState(state: string | null | undefined): boolean {
  return state === "WAITING" || state === "NOTIFIED" || state === "CHECKED_IN";
}

function ActiveStatus({
  status,
  venueId,
  token,
  onReload,
}: {
  status: WaitlistStatus;
  venueId: string;
  token: string;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const state = status.state ?? "WAITING";
  const stateLabel = t(`widget.status.state.${state}`, { defaultValue: state });

  async function doCancel() {
    setBusy(true);
    setError(null);
    try {
      await cancelWaitlistEntry(venueId, token, status.version ?? 0);
      setCancelled(true);
      setConfirming(false);
      onReload();
    } catch (err) {
      if (isStaleWrite(err)) {
        // The notify-vs-cancel race: staff won. Re-render the authoritative state (edge case 6).
        setError(t("widget.status.error.stale"));
        setConfirming(false);
        onReload();
        return;
      }
      setError(t("widget.status.error.cancel"));
    } finally {
      setBusy(false);
    }
  }

  if (cancelled || state === "CANCELLED") {
    return (
      <section aria-labelledby="wl-cancelled" data-testid="waitlist-status-cancelled">
        <h2 id="wl-cancelled">{t("widget.status.cancelled.title")}</h2>
        <p>{t("widget.status.cancelled.body")}</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="wl-title" data-testid="waitlist-status-view">
      <h2 id="wl-title">{t("widget.status.title")}</h2>
      {error ? (
        <p className="rsv-error" role="alert">
          {error}
        </p>
      ) : null}

      <p data-testid="waitlist-status-state" className="rsv-notice">
        {stateLabel}
      </p>

      {state === "NOTIFIED" ? (
        <p data-testid="waitlist-status-ready">{t("widget.status.ready")}</p>
      ) : null}

      {state === "WAITING" ? <Estimate status={status} /> : null}

      {status.partySize ? <p>{t("widget.status.party", { guests: status.partySize })}</p> : null}

      {state === "SEATED" ? <p>{t("widget.status.seated")}</p> : null}

      {(state === "EXPIRED" || state === "NO_SHOW" || state === "DECLINED") &&
      !isOpenState(state) ? (
        <p data-testid="waitlist-status-terminal">{t("widget.status.terminal")}</p>
      ) : null}

      {status.canCancel && !confirming ? (
        <div className="rsv-actions">
          <button
            type="button"
            className="rsv-btn-danger"
            data-testid="waitlist-status-cancel"
            onClick={() => setConfirming(true)}
          >
            {t("widget.status.actions.cancel")}
          </button>
        </div>
      ) : null}

      {confirming ? (
        <div className="rsv-actions" data-testid="waitlist-status-confirm">
          <p>{t("widget.status.cancel.confirm")}</p>
          <button
            type="button"
            className="rsv-btn-danger"
            disabled={busy}
            onClick={() => void doCancel()}
          >
            {t("widget.status.actions.confirmCancel")}
          </button>
          <button
            type="button"
            className="rsv-btn-ghost"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            {t("widget.status.actions.keep")}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** The advisory estimate (R1): "no wait" when nobody fitting is ahead, else "~N ahead / ~X min". */
function Estimate({ status }: { status: WaitlistStatus }) {
  const { t } = useTranslation();
  const ahead = status.partiesAhead;
  if (ahead === null || ahead === undefined || ahead === 0) {
    return <p data-testid="waitlist-status-estimate">{t("widget.status.estimate.noWait")}</p>;
  }
  return (
    <p data-testid="waitlist-status-estimate">
      {t("widget.status.estimate.ahead", { count: ahead })}
      {typeof status.estimatedWaitMinutes === "number"
        ? ` · ${t("widget.status.estimate.minutes", { minutes: status.estimatedWaitMinutes })}`
        : ""}
    </p>
  );
}
